import React from "react";
import {Platform, StyleSheet, Text, TextStyle, View} from "react-native";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";
import {JellyfinItem} from "../../api/jellyfin";
import {playerFont} from "../../theme/tokens";

const EYEBROW_WEIGHT = (Platform.OS === "web" ? "700" : "700") as TextStyle["fontWeight"];
const TITLE_WEIGHT = (Platform.OS === "web" ? "850" : "800") as TextStyle["fontWeight"];
const DESCRIPTION_WEIGHT = (Platform.OS === "web" ? "500" : "500") as TextStyle["fontWeight"];

export function PlayerPausedOverlay({ item, width, height, compact, portraitCompact, shortLandscape }: { item?: JellyfinItem; width: number; height: number; compact: boolean; portraitCompact: boolean; shortLandscape: boolean }) {
  const titleFontSize = compact ? 29 : Math.max(36, Math.min(54, width * 0.038));
  const episodeFontSize = compact ? 17 : Math.max(19, Math.min(27, width * 0.018));
  const overviewFontSize = compact ? 13 : Math.max(14, Math.min(17, width * 0.0115));
  return (
    <View
      pointerEvents="none"
      style={[
        styles.overlay,
        compact && styles.overlayCompact,
        { paddingBottom: portraitCompact ? 65 : shortLandscape ? 62 : Math.max(82, Math.min(128, height * 0.1)) },
      ]}
    >
      <Svg pointerEvents="none" width={width} height={height} style={StyleSheet.absoluteFillObject}>
        <Defs>
          <LinearGradient id="paused-vertical" x1="0" y1="1" x2="0" y2="0">
            <Stop offset="0" stopColor="#000" stopOpacity={compact ? 0.86 : 0.32} />
            <Stop offset="0.54" stopColor="#000" stopOpacity={compact ? 0.62 : 0.19} />
            <Stop offset="1" stopColor="#000" stopOpacity={0.08} />
          </LinearGradient>
          <LinearGradient id="paused-horizontal" x1="0" y1="0" x2="1" y2="0">
            <Stop offset="0" stopColor="#000" stopOpacity={compact ? 0.58 : 0.82} />
            <Stop offset="0.34" stopColor="#000" stopOpacity={compact ? 0.42 : 0.64} />
            <Stop offset="0.68" stopColor="#000" stopOpacity={compact ? 0.27 : 0.2} />
            <Stop offset="1" stopColor="#000" stopOpacity={compact ? 0.12 : 0} />
          </LinearGradient>
        </Defs>
        <Rect width={width} height={height} fill="url(#paused-vertical)" />
        <Rect width={width} height={height} fill="url(#paused-horizontal)" />
      </Svg>
      <View
        style={[
          styles.content,
          compact
            ? { width: Math.max(0, width - 44), marginHorizontal: 22, marginBottom: 150 }
            : {
                width: Math.min(720, width * 0.58),
                marginLeft: Math.max(34, Math.min(64, width * 0.048)),
                transform: [{ translateY: height * 0.04 }],
              },
          shortLandscape && styles.contentShort,
        ]}
      >
        <Text style={styles.label}>You&apos;re watching</Text>
        <Text style={[styles.title, { fontSize: titleFontSize, lineHeight: titleFontSize * 1.02 }]}>
          {item?.SeriesName ?? item?.Name ?? "Untitled"}
        </Text>
        {item?.SeriesName ? <Text style={[styles.episodeTitle, { fontSize: episodeFontSize, lineHeight: episodeFontSize * 1.12 }]}>{item.Name}</Text> : null}
        {item?.Overview ? <Text numberOfLines={3} style={[styles.overview, { fontSize: overviewFontSize, lineHeight: overviewFontSize * 1.35 }]}>{item.Overview}</Text> : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: { ...StyleSheet.absoluteFillObject, zIndex: 18, justifyContent: "center" },
  overlayCompact: { justifyContent: "flex-end" },
  content: { maxWidth: 720 },
  contentShort: { maxWidth: 490 },
  labelCompact: { marginBottom: 8, fontSize: 13, lineHeight: 19 },
  label: { marginBottom: 9, color: "rgba(255,255,255,0.58)", fontFamily: playerFont, fontSize: 14, lineHeight: 20, fontWeight: EYEBROW_WEIGHT, letterSpacing: 0.35 },
  title: { marginBottom: 12, color: "rgba(255,255,255,0.98)", fontFamily: playerFont, fontWeight: TITLE_WEIGHT, letterSpacing: -0.25 },
  episodeMeta: { marginBottom: 22, color: "rgba(255,255,255,0.94)", fontFamily: playerFont, fontWeight: "900" },
  episodeTitle: { marginBottom: 16, color: "rgba(255,255,255,0.90)", fontFamily: playerFont, fontWeight: "700" },
  overview: { maxWidth: 700, color: "rgba(255,255,255,0.70)", fontFamily: playerFont, fontWeight: DESCRIPTION_WEIGHT },
});
